// CLI tab (Agent Platform Phase 5): the kazi command-line tool.
//
// Install, authenticate, first commands, CI recipes — the 5-minute path.
// kazi authenticates as an API client (ideally kind "CLI"), so setup links
// back to the API clients tab for credential creation.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, KeyRound, Terminal } from 'lucide-react';
import { CopyButton } from './common';

const apiBase = () =>
  window.location.hostname.includes('dev.') ? 'https://api.dev.kazipeople.com' : 'https://api.kazipeople.com';

const Snippet: React.FC<{ code: string }> = ({ code }) => (
  <div className="relative">
    <pre className="text-xs bg-muted rounded p-3 overflow-x-auto">{code}</pre>
    <div className="absolute top-2 right-2"><CopyButton text={code} label="" /></div>
  </div>
);

export const CliTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [, setSearchParams] = useSearchParams();

  const setup = `kazi profile create production --api ${apiBase()}
kazi auth login        # paste your client id + secret; stored in the OS keychain
kazi auth status`;

  const firstCommands = `kazi employee list --status Active
kazi leave requests --status pending
kazi payroll runs
kazi payroll preflight <run-id>
kazi events types
kazi events tail --event employee.created
kazi webhooks listen --forward-to http://localhost:3000/webhooks`;

  const ciRecipe = `# CI mode: credentials from the environment — nothing stored.
export KAZI_CLIENT_ID=kpc_live_...
export KAZI_CLIENT_SECRET=kps_live_...
kazi profile create ci --api ${apiBase()}

# Examples
kazi payroll runs --json | jq -r '.data[] | [.periodStart, .state] | @tsv'
kazi webhooks deliveries <subscription-id> --json
kazi api request GET "/public/v1/employees?limit=1"`;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-charcoal flex items-center gap-2">
          <Terminal className="w-4 h-4" />
          {t('developer.cli_title', { defaultValue: 'kazi — the KaziPeople CLI' })}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          {t('developer.cli_intro', {
            defaultValue:
              'KaziPeople from your terminal and your CI pipelines: employees, org, leave, attendance, payroll (read-only), payment visibility (read-only), documents, webhooks, and a live event tail — over the same public API every integration uses.',
          })}
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          {t('developer.cli_policy', {
            defaultValue:
              'By design kazi has no payment execution or payroll approval verbs — those stay in the app under its approval and dual-control rules.',
          })}
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.cli_install', { defaultValue: '1 · Install' })}</h3>
        <p className="text-sm text-muted-foreground">
          {t('developer.cli_install_hint', {
            defaultValue: 'Build from source today (Go 1.25+); packaged releases (Homebrew, binaries) arrive with the public launch.',
          })}
        </p>
        <Snippet code={`git clone https://github.com/amashr-dev/amashr-monorepo && cd amashr-monorepo/cli
go build -o kazi . && sudo mv kazi /usr/local/bin/`} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.cli_auth', { defaultValue: '2 · Authenticate' })}</h3>
        <p className="text-sm text-muted-foreground">
          {t('developer.cli_auth_hint', { defaultValue: 'Create an API client (kind "CLI / scripts") with the scopes you need, then:' })}{' '}
          <button className="text-orange-600 hover:underline inline-flex items-center gap-1"
            onClick={() => setSearchParams({ tab: 'clients' }, { replace: true })}>
            <KeyRound className="w-3.5 h-3.5" />
            {t('developer.cli_open_clients', { defaultValue: 'API clients tab' })}
          </button>
        </p>
        <Snippet code={setup} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.cli_first', { defaultValue: '3 · First commands' })}</h3>
        <Snippet code={firstCommands} />
        <p className="text-xs text-muted-foreground">
          {t('developer.cli_json_hint', { defaultValue: 'Every command supports --json (stable machine output) and -q (IDs only). Exit codes: 3 auth · 4 permission · 5 not found · 6 validation · 7 rate limit.' })}
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="font-medium text-charcoal">{t('developer.cli_ci', { defaultValue: 'CI / automation' })}</h3>
        <Snippet code={ciRecipe} />
        <a
          href="https://www.kazipeople.com/developers/docs/cli"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline"
        >
          {t('developer.cli_docs', { defaultValue: 'Full CLI reference' })} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};
